import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Home,
  User,
  GraduationCap,
  Calendar,
  Edit2,
  Trash2,
  MessageSquare,
  FileText,
  Briefcase,
  BarChart3,
  Plus,
  TrendingUp,
  Award,
  Clock
} from 'lucide-react'
import { Card } from '../components/Card'
import { Button } from '../components/Button'
import { api } from '../lib/axios'
import { EvaluationModal } from './EvaluationModal'

interface Student {
  id: string
  firstName: string
  lastName: string
  birthdate: string | null
  gender: string | null
  classId: string
  class?: {
    id: string
    name: string
    level?: string
  }
}

interface Evaluation {
  id: string
  studentId: string
  respectRegles: number | null
  respectAutres: number | null
  ecoute: number | null
  travail: number | null
  autonomie: number | null
  soin: number | null
  period: string
  evaluationDate: string
  average?: number
  criteriaCount?: number
  createdAt: string
  updatedAt: string
}

interface Comment {
  id: string
  date: string
  content: string
  type: 'positive' | 'negative' | 'neutral'
  teacherName: string
}

export function StudentProfilePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [student, setStudent] = useState<Student | null>(null)
  const [evaluations, setEvaluations] = useState<Evaluation[]>([])
  const [comments, setComments] = useState<Comment[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'overview' | 'evaluations' | 'comments' | 'pfmp' | 'documents'>('overview')
  const [showEvalModal, setShowEvalModal] = useState(false)
  const [editingEvaluation, setEditingEvaluation] = useState<Evaluation | null>(null)

  useEffect(() => {
    if (id) {
      loadStudentData()
    }
  }, [id])

  const loadStudentData = async () => {
    try {
      setLoading(true)
      const [studentResponse, evaluationsResponse] = await Promise.all([
        api.get(`/api/students/${id}`),
        api.get(`/api/evaluations/student/${id}`)
      ])
      
      setStudent(studentResponse.data.student)
      setEvaluations(evaluationsResponse.data.evaluations || [])
      
      // TODO: Charger commentaires
      // const commentsResponse = await api.get(`/api/comments/student/${id}`)
      // setComments(commentsResponse.data.comments || [])
    } catch (error) {
      console.error('Erreur chargement profil:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer définitivement ${student?.firstName} ${student?.lastName} ?`)) return
    
    try {
      await api.delete(`/api/students/${id}`)
      navigate('/students')
    } catch (error) {
      console.error('Erreur suppression:', error)
      alert('Erreur lors de la suppression')
    }
  }

  const openNewEvaluation = () => {
    setEditingEvaluation(null)
    setShowEvalModal(true)
  }

  const openEditEvaluation = (evaluation: Evaluation) => {
    setEditingEvaluation(evaluation)
    setShowEvalModal(true)
  }

  const handleEvaluationSuccess = () => {
    loadStudentData()
    setShowEvalModal(false)
    setEditingEvaluation(null)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Chargement du profil...</p>
        </div>
      </div>
    )
  }

  if (!student) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <Card className="p-12 text-center">
          <p className="text-xl text-gray-600">Élève introuvable</p>
          <Button onClick={() => navigate('/students')} className="mt-4">
            Retour à la liste
          </Button>
        </Card>
      </div>
    )
  }

  const age = student.birthdate
    ? Math.floor((Date.now() - new Date(student.birthdate).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
    : null

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header Navigation */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/students')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour
            </button>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
              title="Accueil"
            >
              <Home className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Header Élève */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-start justify-between">
            {/* Photo + Infos */}
            <div className="flex items-center gap-6">
              {/* Avatar */}
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center text-white font-bold text-3xl shadow-lg">
                {student.firstName[0]}{student.lastName[0]}
              </div>
              
              {/* Identité */}
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">
                  {student.firstName} {student.lastName}
                </h1>
                
                {/* Badges infos */}
                <div className="flex flex-wrap gap-3">
                  {student.class && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg font-medium">
                      <GraduationCap className="w-4 h-4" />
                      {student.class.name} {student.class.level && `- ${student.class.level}`}
                    </div>
                  )}
                  
                  {age !== null && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg font-medium">
                      <Calendar className="w-4 h-4" />
                      {age} ans
                    </div>
                  )}
                  
                  {student.gender && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg font-medium">
                      <User className="w-4 h-4" />
                      {student.gender === 'M' || student.gender === 'Masculin' ? 'Masculin' : 'Féminin'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={() => navigate(`/students/${id}/edit`)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                Modifier
              </Button>
              <Button
                onClick={handleDelete}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Supprimer
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Onglets */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-8">
            <TabButton
              active={activeTab === 'overview'}
              onClick={() => setActiveTab('overview')}
              icon={BarChart3}
              label="Aperçu"
            />
            <TabButton
              active={activeTab === 'evaluations'}
              onClick={() => setActiveTab('evaluations')}
              icon={TrendingUp}
              label="Évaluations"
              badge={evaluations.length}
            />
            <TabButton
              active={activeTab === 'comments'}
              onClick={() => setActiveTab('comments')}
              icon={MessageSquare}
              label="Commentaires"
              badge={comments.length}
            />
            <TabButton
              active={activeTab === 'pfmp'}
              onClick={() => setActiveTab('pfmp')}
              icon={Briefcase}
              label="PFMP (Stages)"
            />
            <TabButton
              active={activeTab === 'documents'}
              onClick={() => setActiveTab('documents')}
              icon={FileText}
              label="Documents"
            />
          </nav>
        </div>
      </div>

      {/* Contenu onglets */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <OverviewTab 
            student={student} 
            evaluations={evaluations}
            onNewEvaluation={openNewEvaluation}
          />
        )}
        {activeTab === 'evaluations' && (
          <EvaluationsTab 
            student={student} 
            evaluations={evaluations}
            onNewEvaluation={openNewEvaluation}
            onEditEvaluation={openEditEvaluation}
          />
        )}
        {activeTab === 'comments' && <CommentsTab student={student} comments={comments} />}
        {activeTab === 'pfmp' && <PFMPTab student={student} />}
        {activeTab === 'documents' && <DocumentsTab student={student} />}
      </div>

      {/* Modal Évaluation */}
      {showEvalModal && student && (
        <EvaluationModal
          student={student}
          existingEvaluation={editingEvaluation}
          onClose={() => {
            setShowEvalModal(false)
            setEditingEvaluation(null)
          }}
          onSuccess={handleEvaluationSuccess}
        />
      )}
    </div>
  )
}

// Composant bouton onglet
function TabButton({
  active,
  onClick,
  icon: Icon,
  label,
  badge
}: {
  active: boolean
  onClick: () => void
  icon: any
  label: string
  badge?: number
}) {
  return (
    <button
      onClick={onClick}
      className={`
        flex items-center gap-2 px-4 py-4 border-b-2 transition-colors font-medium
        ${active
          ? 'border-blue-600 text-blue-600'
          : 'border-transparent text-gray-600 hover:text-gray-900'
        }
      `}
    >
      <Icon className="w-5 h-5" />
      {label}
      {badge !== undefined && badge > 0 && (
        <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">
          {badge}
        </span>
      )}
    </button>
  )
}

// Onglet Aperçu
function OverviewTab({ 
  student, 
  evaluations,
  onNewEvaluation 
}: { 
  student: Student
  evaluations: Evaluation[]
  onNewEvaluation: () => void
}) {
  const latestEval = evaluations[0] // Dernière évaluation
  const average = latestEval?.average || 0

  // Calculer évolution
  const evolution = evaluations.length >= 2
    ? ((evaluations[0].average || 0) - (evaluations[1].average || 0))
    : 0

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Stats rapides */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Dernière évaluation</h3>
          <Award className="w-5 h-5 text-yellow-500" />
        </div>
        <div className="text-4xl font-bold text-blue-600 mb-2">
          {average.toFixed(1)}/4
        </div>
        <p className="text-sm text-gray-600">
          {latestEval ? new Date(latestEval.evaluationDate).toLocaleDateString('fr-FR') : 'Aucune évaluation'}
        </p>
        {latestEval && (
          <p className="text-xs text-gray-500 mt-1">
            {latestEval.period}
          </p>
        )}
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Total évaluations</h3>
          <BarChart3 className="w-5 h-5 text-blue-500" />
        </div>
        <div className="text-4xl font-bold text-gray-900 mb-2">
          {evaluations.length}
        </div>
        <p className="text-sm text-gray-600">Périodes évaluées</p>
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Évolution</h3>
          <TrendingUp className={`w-5 h-5 ${
            evolution > 0 ? 'text-green-500' :
            evolution < 0 ? 'text-red-500' :
            'text-gray-500'
          }`} />
        </div>
        <div className={`text-4xl font-bold mb-2 ${
          evolution > 0 ? 'text-green-600' :
          evolution < 0 ? 'text-red-600' :
          'text-gray-600'
        }`}>
          {evolution > 0 ? '+' : ''}{evolution.toFixed(1)}
        </div>
        <p className="text-sm text-gray-600">Points depuis dernière éval</p>
      </Card>

      {/* Dernière évaluation détaillée */}
      <div className="lg:col-span-3">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900">Dernière évaluation détaillée</h3>
            <Button 
              onClick={onNewEvaluation}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              <Plus className="w-4 h-4" />
              Nouvelle évaluation
            </Button>
          </div>

          {latestEval ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { label: 'Respect règles', value: latestEval.respectRegles },
                { label: 'Respect autres', value: latestEval.respectAutres },
                { label: 'Écoute', value: latestEval.ecoute },
                { label: 'Travail', value: latestEval.travail },
                { label: 'Autonomie', value: latestEval.autonomie },
                { label: 'Soin', value: latestEval.soin }
              ].map((criteria) => (
                <div key={criteria.label} className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">{criteria.label}</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {criteria.value !== null ? `${criteria.value}/4` : 'N/A'}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Aucune évaluation pour le moment</p>
              <Button 
                onClick={onNewEvaluation}
                className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
              >
                Créer la première évaluation
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}

// Onglet Évaluations
function EvaluationsTab({ 
  student, 
  evaluations,
  onNewEvaluation,
  onEditEvaluation
}: { 
  student: Student
  evaluations: Evaluation[]
  onNewEvaluation: () => void
  onEditEvaluation: (evaluation: Evaluation) => void
}) {
  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">Historique des évaluations</h3>
          <Button 
            onClick={onNewEvaluation}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            <Plus className="w-4 h-4" />
            Nouvelle évaluation
          </Button>
        </div>

        {evaluations.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Aucune évaluation enregistrée</p>
          </div>
        ) : (
          <div className="space-y-4">
            {evaluations.map((evaluation) => (
              <div 
                key={evaluation.id} 
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => onEditEvaluation(evaluation)}
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold text-gray-900">
                      {new Date(evaluation.evaluationDate).toLocaleDateString('fr-FR', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                    <p className="text-sm text-gray-600">{evaluation.period}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-blue-600">
                      {evaluation.average?.toFixed(1)}/4
                    </div>
                    <p className="text-xs text-gray-500">
                      {evaluation.criteriaCount || 0}/6 critères
                    </p>
                  </div>
                </div>
                
                {/* Barre progression */}
                <div className="mt-3">
                  <div className="flex gap-2">
                    {[
                      { label: 'Respect règles', value: evaluation.respectRegles },
                      { label: 'Respect autres', value: evaluation.respectAutres },
                      { label: 'Écoute', value: evaluation.ecoute },
                      { label: 'Travail', value: evaluation.travail },
                      { label: 'Autonomie', value: evaluation.autonomie },
                      { label: 'Soin', value: evaluation.soin }
                    ].map((criteria, index) => (
                      <div key={index} className="flex-1">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all ${
                              criteria.value === 4 ? 'bg-green-500' :
                              criteria.value === 3 ? 'bg-blue-500' :
                              criteria.value === 2 ? 'bg-orange-500' :
                              criteria.value === 1 ? 'bg-red-500' :
                              'bg-gray-300'
                            }`}
                            style={{ width: `${((criteria.value || 0) / 4) * 100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}

// Onglet Commentaires
function CommentsTab({ student, comments }: { student: Student; comments: Comment[] }) {
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-900">Observations et commentaires</h3>
        <Button className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg">
          <Plus className="w-4 h-4" />
          Ajouter un commentaire
        </Button>
      </div>

      {comments.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>Aucun commentaire pour le moment</p>
        </div>
      ) : (
        <div className="space-y-4">
          {comments.map((comment) => (
            <div key={comment.id} className="border-l-4 border-blue-500 pl-4 py-2">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">
                  {new Date(comment.date).toLocaleDateString('fr-FR')} - {comment.teacherName}
                </p>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  comment.type === 'positive' ? 'bg-green-100 text-green-700' :
                  comment.type === 'negative' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {comment.type}
                </span>
              </div>
              <p className="text-gray-900">{comment.content}</p>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

// Onglet PFMP
function PFMPTab({ student }: { student: Student }) {
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-900">Périodes de Formation en Milieu Professionnel</h3>
        <Button className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg">
          <Plus className="w-4 h-4" />
          Ajouter un stage
        </Button>
      </div>

      <div className="text-center py-12 text-gray-500">
        <Briefcase className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p>Aucun stage enregistré</p>
        <p className="text-sm mt-2">Module PFMP disponible prochainement</p>
      </div>
    </Card>
  )
}

// Onglet Documents
function DocumentsTab({ student }: { student: Student }) {
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-900">Documents et bulletins</h3>
        <Button className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg">
          <Plus className="w-4 h-4" />
          Ajouter un document
        </Button>
      </div>

      <div className="text-center py-12 text-gray-500">
        <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p>Aucun document disponible</p>
      </div>
    </Card>
  )
}
